/**
 * CasesWorking Component
 * 
 * Displays and manages active/working support cases from Supabase.
 * These are cases that are currently being processed and reviewed.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import './Cases.css';

/**
 * Parse case content string into structured object
 */
function parseContent(content) {
  if (!content) {
    return { theme: 'Нет данных', question: '', answer: '' };
  }
  const themeMatch = content.match(/Тема:(.*?);/);
  const questionMatch = content.match(/Вопрос:(.*?);/);
  const answerMatch = content.match(/Ответ:(.*)/s);

  const theme = themeMatch ? themeMatch[1].trim() : 'Тема не найдена';
  const question = questionMatch ? questionMatch[1].trim() : '';
  const answer = answerMatch ? answerMatch[1].trim() : '';

  if (theme === 'Тема не найдена' && !question && !answer) {
    return { theme: content, question: '', answer: '' };
  }
  return { theme, question, answer };
}

function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      {message}
    </div>
  );
}

export default function CasesWorking() {
  const [data, setData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [editedTheme, setEditedTheme] = useState('');
  const [editedQuestion, setEditedQuestion] = useState('');
  const [editedAnswer, setEditedAnswer] = useState('');
  
  const [toast, setToast] = useState(null);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const [caseAnalysis, setCaseAnalysis] = useState({
    case_source: 'Telegram',
    case_number: 'TG-12345',
    initial_user_request: 'Пользователь не может найти кнопку для скачивания отчета.',
    user_conclusions: 'Пользователь считает, что интерфейс неудобный и кнопка должна быть более заметной.',
  });

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  useEffect(() => {
    async function getCheckCasesData() {
      const { data, error } = await supabase.from('checkcases').select('id, content');
      if (error) {
        console.error('Error fetching data from Supabase:', error);
        showToast('Не удалось загрузить рабочие кейсы.', 'error');
        setData([]);
      } else {
        setData(data || []);
      }
    }
    getCheckCasesData();
  }, []);

  const parsedSelectedItem = useMemo(() => 
    selectedItem ? parseContent(selectedItem.content) : null, 
    [selectedItem]
  );

  const handleCardClick = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
    setIsEditMode(false);
  };

  const handleEditClick = () => {
    if (parsedSelectedItem) {
      setEditedTheme(parsedSelectedItem.theme);
      setEditedQuestion(parsedSelectedItem.question);
      setEditedAnswer(parsedSelectedItem.answer);
      setIsEditMode(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedItem) return;

    const newContent = `Тема: ${editedTheme}; Вопрос: ${editedQuestion}; Ответ: ${editedAnswer}`;
    const { data: updatedData, error } = await supabase
      .from('checkcases')
      .update({ content: newContent })
      .eq('id', selectedItem.id)
      .select();

    if (error) {
      console.error('Error updating data:', error);
      showToast('Не удалось сохранить изменения.', 'error');
    } else if (updatedData) {
      setData(prevData => prevData.map(item => 
        item.id === selectedItem.id ? { ...item, content: newContent } : item
      ));
      setSelectedItem(prevItem => prevItem ? { ...prevItem, content: newContent } : null);
      setIsEditMode(false);
      showToast('Кейс успешно обновлен.', 'success');
    }
  };
  
  const handleStatusChange = (status) => {
    if (!selectedItem) return;
    setIsModalOpen(false);

    switch (status) {
      case 'ok':
        showToast('Кейс будет удален из таблицы `checkcases`.', 'info');
        break;
      case 'not_ok':
        showToast('Кейс будет перемещен в таблицу `complexcases`.', 'info');
        break;
      case 'pending':
        showToast('Кейс будет перемещен в таблицу `deferredcases`.', 'info');
        break;
      case 'think':
        showToast('Действие в разработке.', 'info');
        break;
      default:
        break;
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setIsAccordionOpen(false);
  };

  return (
    <div className="cases-container">
      {toast && (
        <div className="toast-container">
          <Toast 
            message={toast.message} 
            type={toast.type} 
            onClose={() => setToast(null)} 
          />
        </div>
      )}

      <h1 className="cases-title">Рабочие кейсы</h1>
      
      {data.length > 0 ? (
        <div className="cases-grid">
          {data.map((item) => (
            <div 
              key={item.id}
              className="case-card"
              onClick={() => handleCardClick(item)}
            >
              <h3 className="case-card-title">{parseContent(item.content).theme}</h3>
            </div>
          ))}
        </div>
      ) : (
        <p className="cases-loading">Кейсы грузятся, секундочку....</p>
      )}

      {isModalOpen && selectedItem && parsedSelectedItem && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}>
          <div className="modal-content">
            <div className="modal-header">
              <h2 className="modal-title">Детали кейса</h2>
            </div>
            
            <div className="modal-body">
              {isEditMode ? (
                <div>
                  <div className="case-detail-section">
                    <label className="case-detail-label">Тема:</label>
                    <input 
                      type="text"
                      value={editedTheme} 
                      onChange={(e) => setEditedTheme(e.target.value)} 
                      className="cases-input" 
                    />
                  </div>
                  <div className="case-detail-section">
                    <label className="case-detail-label">Вопрос:</label>
                    <textarea 
                      value={editedQuestion} 
                      onChange={(e) => setEditedQuestion(e.target.value)} 
                      className="cases-textarea" 
                      rows={5} 
                    />
                  </div>
                  <div className="case-detail-section">
                    <label className="case-detail-label">Ответ:</label>
                    <textarea 
                      value={editedAnswer} 
                      onChange={(e) => setEditedAnswer(e.target.value)} 
                      className="cases-textarea" 
                      rows={8} 
                    />
                  </div>
                </div>
              ) : (
                <div>
                  <div className="case-detail-section">
                    <h3 className="case-detail-label">Тема:</h3>
                    <p className="case-detail-text">{parsedSelectedItem.theme}</p>
                  </div>
                  {parsedSelectedItem.question && (
                    <div className="case-detail-section">
                      <h3 className="case-detail-label">Вопрос:</h3>
                      <p className="case-detail-text">{parsedSelectedItem.question}</p>
                    </div>
                  )}
                  {parsedSelectedItem.answer && (
                    <div className="case-detail-section">
                      <h3 className="case-detail-label">Ответ:</h3>
                      <p className="case-detail-text">{parsedSelectedItem.answer}</p>
                    </div>
                  )}
                </div>
              )}

              <div className="accordion">
                <div className="accordion-item">
                  <button 
                    className={`accordion-trigger ${isAccordionOpen ? 'open' : ''}`}
                    onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                  >
                    Пост-анализ для эксперта
                  </button>
                  <div className={`accordion-content ${isAccordionOpen ? '' : 'hidden'}`}>
                    <div className="analysis-section">
                      <h4 className="analysis-title">Анализ кейса</h4>
                      <div className="analysis-grid">
                        <div className="analysis-field">
                          <label className="analysis-label">Источник</label>
                          <input 
                            type="text"
                            value={caseAnalysis.case_source} 
                            onChange={e => setCaseAnalysis({...caseAnalysis, case_source: e.target.value})} 
                            className="cases-input" 
                          />
                        </div>
                        <div className="analysis-field">
                          <label className="analysis-label">Номер кейса</label>
                          <input 
                            type="text"
                            value={caseAnalysis.case_number} 
                            onChange={e => setCaseAnalysis({...caseAnalysis, case_number: e.target.value})} 
                            className="cases-input" 
                          />
                        </div>
                      </div>
                      <div className="analysis-field">
                        <label className="analysis-label">Изначальный запрос пользователя</label>
                        <textarea 
                          value={caseAnalysis.initial_user_request} 
                          onChange={e => setCaseAnalysis({...caseAnalysis, initial_user_request: e.target.value})} 
                          className="cases-textarea" 
                        />
                      </div>
                      <div className="analysis-field">
                        <label className="analysis-label">Выводы пользователя</label>
                        <textarea 
                          value={caseAnalysis.user_conclusions} 
                          onChange={e => setCaseAnalysis({...caseAnalysis, user_conclusions: e.target.value})} 
                          className="cases-textarea" 
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              {isEditMode ? (
                <>
                  <div></div>
                  <div className="modal-actions-right">
                    <button onClick={() => setIsEditMode(false)} className="btn btn-outline">Отмена</button>
                    <button onClick={handleSaveEdit} className="btn btn-primary">Сохранить</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="modal-actions-left">
                    <button onClick={handleEditClick} className="btn btn-secondary">Редактировать</button>
                  </div>
                  <div className="modal-actions-right">
                    <button onClick={() => handleStatusChange('ok')} className="btn btn-success">ОК</button>
                    <button onClick={() => handleStatusChange('not_ok')} className="btn btn-danger">не ОК</button>
                    <button onClick={() => handleStatusChange('pending')} className="btn btn-warning">Отложить</button>
                    <button onClick={() => handleStatusChange('think')} className="btn btn-info">Подумать</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
